import {
  Box,
  Circle,
  Grid,
  HStack,
  Input,
  Link,
  Spacer,
  Stack,
  Text,
  createListCollection,
} from "@chakra-ui/react";
import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import {
  InviteWorkspaceMemberData,
  WorkspaceMemberDetails,
  getWorkspaceMembers,
  inviteWorkspaceMember,
  updateWorkspace,
} from "../../api/workspace";
import { useBreadcrumbs } from "../../components/breadcrumbs";
import { Button } from "../../components/button";
import Page from "../../components/page";
import PageBody from "../../components/page-body";
import PageHeader from "../../components/page-header";
import RegionTimezoneSelect from "../../components/region-timezone-select";
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
} from "../../components/select";
import { StatusPill } from "../../components/status-pill";
import { toaster } from "../../components/toaster";
import {
  useAppDispatch,
  useAppSelector,
  useLoading,
  useToaster,
  useWorkspaceURL,
} from "../../hooks";
import { capitalize } from "../../lib/capitalize";
import { checkUser } from "../../store/authSlice";
import { setSlug } from "../../store/workspaceSlice";
import { Workspace } from "../../types/workspace";

interface WorkspaceForm {
  name: string;
  slug: string;
  timezone: string;
}

export default function GeneralSettingsPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { workspace: slug } = useParams();
  const { workspaces } = useAppSelector((state) => state.auth.user);
  const workspace = React.useMemo(
    () => workspaces.find((w) => w.slug === slug),
    [slug, workspaces],
  );
  useBreadcrumbs([{ label: "Settings" }]);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<WorkspaceForm>({
    defaultValues: {
      name: workspace.name,
      slug: workspace.slug,
      timezone: workspace.timezone || "UTC",
    },
  });
  const onSubmit = handleSubmit(
    async (data: Omit<Workspace, "domain" | "id">) => {
      setIsUpdating(true);
      try {
        const updatedWorkspace = { ...workspace, ...data };
        await updateWorkspace(workspace.slug, updatedWorkspace);
        await dispatch(checkUser()).unwrap();
        dispatch(setSlug(workspace.slug));
        navigate(`/workspaces/${updatedWorkspace.slug}/settings`);
      } finally {
        setIsUpdating(false);
      }
    },
  );
  return (
    <Page>
      <Stack flex="1" gap="6">
        <PageHeader title="Settings" subtitle="Workspace and members" />
        <PageBody>
          <Stack gap="4" maxWidth={"760px" as any} width="full">
            <SettingsCard title="Workspace">
              <form onSubmit={onSubmit}>
                <Stack gap="2">
                  <FormRow label="Name" error={errors.name?.message}>
                    <Input
                      {...register("name", { required: "A name is required." })}
                    />
                  </FormRow>
                  <FormRow label="Slug" error={errors.slug?.message}>
                    <Input
                      {...register("slug", { required: "A slug is required." })}
                    />
                  </FormRow>
                  <FormRow label="Region" error={errors.timezone?.message}>
                    <Controller
                      name="timezone"
                      control={control}
                      rules={{ required: "A region is required." }}
                      render={({ field }) => (
                        <RegionTimezoneSelect
                          value={field.value}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          placeholder="Select a region"
                        />
                      )}
                    />
                  </FormRow>
                  <HStack marginTop="2">
                    <Spacer />
                    <Button loading={isUpdating} type="submit" variant="solid">
                      Update
                    </Button>
                  </HStack>
                </Stack>
              </form>
            </SettingsCard>
            {workspace && <WorkspaceMembersSection workspace={workspace} />}
            <CompanyKnowledgeSection />
          </Stack>
        </PageBody>
      </Stack>
    </Page>
  );
}

function SettingsCard({
  title,
  sub,
  children,
}: {
  title: string;
  sub?: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      background="white"
      borderRadius="l3"
      boxShadow="card"
      overflow="hidden"
    >
      <Box
        borderBottomWidth="1"
        borderColor="border.soft"
        paddingX="6"
        paddingY="4"
      >
        <Text color="fg.ink" fontSize="md" fontWeight="medium">
          {title}
        </Text>
        {sub && (
          <Text color="fg.muted" fontSize="sm" marginTop="0.5">
            {sub}
          </Text>
        )}
      </Box>
      <Box paddingBottom="6" paddingTop="5" paddingX="6">
        {children}
      </Box>
    </Box>
  );
}

function FormRow({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <Grid
      alignItems="center"
      gap={"18px" as any}
      gridTemplateColumns={"130px 1fr" as any}
      paddingY="2"
    >
      <Text color="fg.muted" fontSize="sm">
        {label}
      </Text>
      <Stack gap="1">
        {children}
        {error && (
          <Text color="red.500" fontSize="xs">
            {error}
          </Text>
        )}
      </Stack>
    </Grid>
  );
}

interface WorkspaceMembersSectionProps {
  workspace: Workspace;
}

function WorkspaceMembersSection({ workspace }: WorkspaceMembersSectionProps) {
  const [isLoading, withLoading] = useLoading<void>(true);
  const [members, setMembers] = React.useState<WorkspaceMemberDetails[]>([]);
  const withToaster = useToaster();
  const loadMembers = React.useCallback(
    () =>
      withLoading(async () => {
        setMembers(await getWorkspaceMembers(workspace.slug));
      }),
    [withLoading, workspace],
  );
  const inviteMember = React.useCallback(
    (data: InviteWorkspaceMemberData) =>
      withLoading(async () => {
        await inviteWorkspaceMember(workspace.slug, data);
        setMembers(await getWorkspaceMembers(workspace.slug));
      }),
    [withLoading, workspace],
  );
  const resendInvite = React.useCallback(
    (member: WorkspaceMemberDetails) =>
      withToaster(async () => {
        await inviteWorkspaceMember(workspace.slug, {
          email: member.username,
          role: member.role,
        });
        toaster.create({ type: "success", title: "Invite sent" });
      }),
    [withToaster, workspace],
  );

  React.useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  return (
    <SettingsCard title="Members" sub="Manage who can access your workspace.">
      <Stack gap="1">
        <InviteMemberView onSubmit={inviteMember} />
        {!isLoading && (
          <Stack gap="0" marginTop="3">
            {members.map((m, index) => (
              <HStack
                key={m.username}
                borderColor="border.soft"
                borderTopWidth={index > 0 ? "1" : "0"}
                gap="3"
                paddingY={"13px" as any}
              >
                <Circle
                  background="meterTrack"
                  color="navy"
                  fontFamily="inter"
                  fontSize={"13px" as any}
                  fontWeight="semibold"
                  size="8"
                  textTransform="uppercase"
                >
                  {m.username.charAt(0)}
                </Circle>
                <HStack flex="1" gap="2.5" minWidth="0">
                  <Text color="fg" fontSize="sm" truncate>
                    {m.username}
                  </Text>
                  {m.status === "invited" && (
                    <Link
                      color="fg.muted"
                      fontSize={"13px" as any}
                      textDecoration="underline"
                      textUnderlineOffset={"2px" as any}
                      onClick={() => resendInvite(m)}
                      _hover={{ color: "fg.ink" }}
                    >
                      Resend invite
                    </Link>
                  )}
                </HStack>
                <Text
                  color="fg.muted"
                  flexShrink="0"
                  fontSize={"13px" as any}
                  width={"70px" as any}
                >
                  {capitalize(m.role)}
                </Text>
                <Box flexShrink="0" width={"96px" as any}>
                  <StatusPill
                    tone={
                      m.status === "active"
                        ? "green"
                        : m.status === "invited"
                          ? "blue"
                          : "gray"
                    }
                  >
                    {capitalize(m.status)}
                  </StatusPill>
                </Box>
              </HStack>
            ))}
          </Stack>
        )}
      </Stack>
    </SettingsCard>
  );
}

interface InviteMemberForm {
  email: string;
  role: string;
}

interface InviteMemberViewProps {
  onSubmit: (data: InviteWorkspaceMemberData) => Promise<void>;
}

function InviteMemberView(props: InviteMemberViewProps) {
  const { control, register, handleSubmit } = useForm<InviteMemberForm>({
    defaultValues: { role: "member" },
  });
  const onSubmit = handleSubmit(({ email, role }) => {
    props.onSubmit({ email, role });
  });

  const rolesCollection = React.useMemo(() => {
    return createListCollection({
      items: [
        { value: "member", label: "Member" },
        { value: "admin", label: "Admin" },
      ],
    });
  }, []);

  return (
    <form onSubmit={onSubmit}>
      <HStack gap="2">
        <Input
          flex="1"
          placeholder="user@example.com"
          type="email"
          {...register("email", {
            required: "An email address is required.",
          })}
        />
        <Controller
          name="role"
          control={control}
          render={({ field }) => (
            <SelectRoot
              size="sm"
              value={[field.value]}
              onValueChange={({ value }) => field.onChange(value[0])}
              onBlur={field.onBlur}
              collection={rolesCollection}
              width={"120px" as any}
            >
              <SelectTrigger>
                <SelectValueText />
              </SelectTrigger>
              <SelectContent>
                {rolesCollection.items.map((role) => (
                  <SelectItem key={role.value} item={role}>
                    {role.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </SelectRoot>
          )}
        />
        <Button type="submit" variant="solid">
          Invite
        </Button>
      </HStack>
    </form>
  );
}

function CompanyKnowledgeSection() {
  const navigate = useNavigate();
  const workspaceURL = useWorkspaceURL();
  return (
    <SettingsCard
      title="Company Knowledge"
      sub="Describe your company, customers, competitors, and brand so the product can act on your behalf."
    >
      <HStack marginTop="1">
        <Spacer />
        <Button
          onClick={() => navigate(workspaceURL("settings/company-knowledge"))}
          variant="solid"
        >
          Manage knowledge
        </Button>
      </HStack>
    </SettingsCard>
  );
}
